// Kiểu dữ liệu + tiện ích cho màn Bảng giá của đơn vị (Đại lý / CTV / Shop).

import { ApiError } from '@/lib/api'
import type { PriceGrid, PriceListData } from '@/components/PriceListEditor'

export type UnitType = 'agency' | 'ctv' | 'shop'
export type LibraryKind = 'ctv' | 'shop'

export const KIND_LABEL: Record<string, string> = { cost: 'Giá gốc', agency: 'Đại lý', ctv: 'CTV', shop: 'Shop' }

export interface LibraryRow {
  id: string
  code: string
  name: string
  kind: LibraryKind
  ownerShopId: string | null
  gridVersion: number
  version: number
  data: PriceListData
  /** Hàng cân của bảng (máy chủ trả kèm). */
  grid?: PriceGrid
  archivedAt: string | null
  createdAt: string
  updatedAt: string
  unitCount: number
}

export interface PriceListVersion {
  version: number
  gridVersion: number
  effectiveFrom: string
  changeNote: string | null
  createdAt: string
  data: PriceListData
  grid?: PriceGrid
}

export interface LibraryDetail extends Omit<LibraryRow, 'unitCount'> {
  versions: PriceListVersion[]
  units: Array<{ id: string; name: string; tenantType: string; status: string }>
}

export interface MineResponse {
  mine: null | {
    priceList: { id: string; code: string; name: string; kind: string }
    current: null | { version: number; gridVersion: number; effectiveFrom: string; data: PriceListData; changeNote: string | null; grid: PriceGrid }
    pending: Array<{ version: number; effectiveFrom: string; data: PriceListData; changeNote: string | null; grid?: PriceGrid }>
  }
}

export interface NetworkUnit {
  id: string
  name: string
  tenantType: UnitType
  parId: string | null
  direct: boolean
  status: string
  priceList: { code: string; name: string; mine: boolean } | null
}

/** Ô giá trả về từ API (BelowCell). */
export interface PriceCell {
  zone: string
  cell: number | 'step'
  label: string
  child: number
  parent: number
}

export interface SaveResponse {
  priceList: Omit<LibraryRow, 'unitCount'>
  warnings: PriceCell[]
  scheduled?: string | null
}

export function errorDetails<T>(e: unknown, code: string): T | null {
  return e instanceof ApiError && e.code === code ? ((e.details ?? {}) as T) : null
}

export function errorMessage(e: unknown, fallback = 'Có lỗi xảy ra'): string {
  return e instanceof Error && e.message ? e.message : fallback
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('vi-VN', { dateStyle: 'short', timeStyle: 'short' })
}

/**
 * Bản thay chỗ `v` (số lớn hơn, hiệu lực không muộn hơn) — có thì `v` không bao giờ được áp,
 * dù ngày hẹn chưa tới. Cùng quy tắc máy chủ (packages/common/src/price-list-rules.ts · supersededBy).
 */
export function supersededBy(versions: PriceListVersion[], v: PriceListVersion): PriceListVersion | null {
  const at = new Date(v.effectiveFrom).getTime()
  let by: PriceListVersion | null = null
  for (const w of versions) if (w.version > v.version && new Date(w.effectiveFrom).getTime() <= at && (!by || w.version > by.version)) by = w
  return by
}

export function effectiveVersionNumber(versions: PriceListVersion[], now = Date.now()): number | null {
  let best: number | null = null
  for (const v of versions) {
    if (new Date(v.effectiveFrom).getTime() <= now && (best === null || v.version > best)) best = v.version
  }
  return best
}

export function toLocalInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}

/** `earliest` + 5 phút, làm tròn lên 5 phút — để lần gửi lại không hụt vài giây. */
export function scheduleFrom(earliestIso: string): Date {
  const step = 5 * 60_000
  const t = new Date(earliestIso).getTime() + step
  return new Date(Math.ceil(t / step) * step)
}

export function sameData(a: PriceListData, b: PriceListData): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}
